import { axiosConfig } from "@/services/axiosConfig";
import { get, handleRequestError, type RequestConfig } from "@/services/axiosUitils";
import {
  buildGlucoseReadingLogPayload,
  glucoseReadingsFromApi,
  glucoseReadingsToApiPayload,
  glucoseReadingsUiHasAny,
  type GlucoseReadingLogPayload,
  type GlucoseReadingsUi,
  type GlucoseReadingsApiPayload,
} from "../utils/glucoseReadingsModel";

const BASE = "/food_scanner/glucose-readings/";

export interface GlucoseReadingsApiEnvelope {
  success?: { code: string; message: string };
  readings?: GlucoseReadingsApiPayload;
  /** Flat response (production API) */
  cgm?: number | null;
  finger_prick?: number | null;
  fasting?: number | null;
  hba1c?: number | null;
}

class GlucoseReadingsService {
  private static instance: GlucoseReadingsService;

  private constructor() {}

  public static getInstance(): GlucoseReadingsService {
    if (!GlucoseReadingsService.instance) {
      GlucoseReadingsService.instance = new GlucoseReadingsService();
    }
    return GlucoseReadingsService.instance;
  }

  /**
   * GET latest readings. Returns null if the request fails or response is empty
   * (Home page can fall back to localStorage).
   */
  async getReadings(config?: { showNotification?: boolean }): Promise<GlucoseReadingsUi | null> {
    const response = await get<GlucoseReadingsApiEnvelope>(BASE, {
      showNotification: false,
      ...config,
    });
    if (response === undefined || response === null) return null;
    const ui = glucoseReadingsFromApi(response);
    return ui;
  }

  /**
   * PUT a single reading log — partial body with meal_context and optional entered_at.
   * Omits entered_at for auto server timestamp (LOG NOW).
   */
  async saveReadingLog(
    input: {
      readingType: "finger_prick" | "cgm" | "fasting" | "hba1c";
      value: number;
      mealTiming?: "pre-meal" | "post-meal";
      enteredAt?: string;
    },
    config?: RequestConfig,
  ): Promise<GlucoseReadingsUi | null> {
    const body = buildGlucoseReadingLogPayload(input);
    return this.putPayload(body, config);
  }

  /**
   * PUT full readings snapshot (legacy / bulk update).
   */
  async saveReadings(ui: GlucoseReadingsUi, config?: RequestConfig): Promise<GlucoseReadingsUi | null> {
    const body = glucoseReadingsToApiPayload(ui);
    return this.putPayload(body, config);
  }

  private async putPayload(
    body: GlucoseReadingLogPayload | GlucoseReadingsApiPayload,
    config?: RequestConfig,
  ): Promise<GlucoseReadingsUi | null> {
    try {
      const res = await axiosConfig.put<GlucoseReadingsApiEnvelope>(BASE, body, config);
      if (res.status < 200 || res.status >= 300) return null;
      const data = res.data;
      if (data != null && typeof data === "object" && !Array.isArray(data)) {
        const parsed = glucoseReadingsFromApi(data);
        if (glucoseReadingsUiHasAny(parsed)) return parsed;
      }
      return null;
    } catch (error: unknown) {
      handleRequestError(error, config);
      return null;
    }
  }
}

export default GlucoseReadingsService.getInstance();
